// FieldVision Admin - Mark Upload Section Component
// Container komponent for upload af analysekort til ordrer

import React, { useState, useEffect, useCallback } from 'react';
import MarkUploadRow, { MapFile } from './MarkUploadRow';
import { getMaps, MapsResponse } from '../services/api-additions';

// ============================================================================
// Types
// ============================================================================

interface Field {
  Id: number;
  Marknr: string;
  Areal: number;
  CVR?: string;
}

interface MarkUploadSectionProps {
  orderId: number;
  orderStatus: string;
  fields: Field[];
}

// ============================================================================
// Component
// ============================================================================

const MarkUploadSection: React.FC<MarkUploadSectionProps> = ({
  orderId,
  orderStatus,
  fields,
}) => {
  const [maps, setMaps] = useState<MapFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Fetch maps from API
  const fetchMaps = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const response: MapsResponse = await getMaps(orderId);
      setMaps(response.maps as MapFile[]);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Kunne ikke hente kort';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Load maps on mount
  useEffect(() => {
    if (orderStatus === 'Færdig' || orderStatus === 'Fløjet') {
      fetchMaps();
    }
  }, [orderStatus, fetchMaps]);

  // Handlers for child components
  const handleUploadComplete = (): void => {
    fetchMaps();
  };

  const handleDeleteComplete = (): void => {
    fetchMaps();
  };

  // Show for Fløjet (awaiting upload) and Færdig (view uploaded maps)
  if (orderStatus !== 'Færdig' && orderStatus !== 'Fløjet') {
    return null;
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Upload Analysekort</h3>
      <p style={styles.description}>
        Træk ZIP-filer til de relevante marker. Filnavn format: CVR_Marknr_Type_Terminal.zip
        (f.eks. 26125871_4-0_Ukrudt_JohnDeere.zip).
      </p>

      {/* Loading state */}
      {loading && (
        <div style={styles.loadingBox}>
          Henter eksisterende kort...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={styles.errorBox}>
          <span>{error}</span>
          <button onClick={fetchMaps} style={styles.retryButton}>
            Prøv igen
          </button>
        </div>
      )}

      {/* Field rows */}
      {!loading && !error && (
        <div style={styles.fieldList}>
          {fields.map((field) => (
            <MarkUploadRow
              key={field.Id}
              orderId={orderId}
              marknr={field.Marknr}
              areal={field.Areal}
              cvr={field.CVR}
              existingMaps={maps}
              onUploadComplete={handleUploadComplete}
              onDeleteComplete={handleDeleteComplete}
            />
          ))}
        </div>
      )}

      {/* Summary */}
      {!loading && !error && maps.length > 0 && (
        <div style={styles.summary}>
          <span style={styles.summaryIcon}>i</span>
          <span style={styles.summaryText}>
            {maps.length} kort uploadet til denne ordre
          </span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Styles
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '24px',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  description: {
    margin: '0 0 16px 0',
    fontSize: '13px',
    color: '#9ca3af',
  },
  loadingBox: {
    padding: '20px',
    textAlign: 'center',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  errorBox: {
    padding: '16px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  retryButton: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#dc2626',
    backgroundColor: 'white',
    border: '1px solid #dc2626',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  fieldList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  summary: {
    marginTop: '16px',
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  summaryIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#16a34a',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  summaryText: {
    fontSize: '13px',
    color: '#16a34a',
    fontWeight: 500,
  },
};

export default MarkUploadSection;
